// RoundtableDashboard.js
// Coursera-branded ABM activity dashboard card (Roundtable)
// - Zero external deps
// - Prepopulated with your Dec 11, 2025 Panelle roundtable details
// - Includes KPIs, tiny Conduit “meter”, speaker list, attendee table

import React, { useMemo } from "react";

/* ============ Brand tokens ============ */
const T = {
  blue500: "#2E89FF",
  blue700: "#2A73CC",
  blue900: "#1C3F7A",
  blue975: "#0E2B57",
  line: "#E6E7EB",
  ink: "#0B1A3C",
  muted: "#5C6570",
  card: "#FFFFFF",
  bg: "#F5F7FA",
};

/* ============ Event data (prepopulated) ============ */
const EVENT = {
  title: "Roundtable — Building AI-Ready Skills: Women Leading Transformation",
  date: "Thu, Dec 11, 2025 • 12:30–14:00 ET",
  track: "All Industries • North America",
  partner: "Panelle (in partnership with Coursera)",
  topics: ["AI", "Digital Transformation", "DEI"],
  cap: 12, // soft cap for quality discussion
  speakers: [
    {
      name: "Sarah Tijou",
      role: "Senior Vice President, Panelle",
      kind: "Moderator",
    },
    {
      name: "Trena Minudri",
      role: "VP & Chief Learning Officer, Coursera",
      kind: "Partner",
    },
    {
      name: "Ashley Nicol",
      role: "Director, Enterprise Sales, Coursera",
      kind: "Partner",
    },
    {
      name: "Aya Ladki",
      role: "Director, Cloud, Data, AI, PwC",
      kind: "Speaker",
    },
    {
      name: "Prapti Sen",
      role: "Chief of Staff – Engineering, Adobe",
      kind: "Speaker",
    },
    {
      name: "KT Johnson",
      role: "VP – Sr. Product Mgr, HR Digital Experiences & Solutions, T. Rowe Price",
      kind: "Speaker",
    },
    { name: "Anne Higgins", role: "Head of Cyber Risk, BNY", kind: "Speaker" },
  ],
  // Registrants (job title + company) — pulled from your list
  attendees: [
    { title: "Chief of Staff - Engineering", company: "Adobe" },
    {
      title: "Senior Director, Enterprise Data and Machine Learning",
      company: "Capital One",
    },
    {
      title:
        "Vice President - Senior Product Manager, HR Digital Experiences & Solutions",
      company: "T Rowe Price",
    },
    { title: "Head of Cyber Risk", company: "BNY" },
    { title: "Director, Master Data Office", company: "Ross Stores" },
    { title: "VP Enterprise Connected Data Strategy", company: "Google" },
    { title: "Chief Data Office - Head of Product", company: "IBM" },
    { title: "Director, Engineering, Chief Data Office", company: "IBM" },
    {
      title: "Head of Enterprise Data Governance",
      company: "Compass Group USA",
    },
    { title: "CIO", company: "BMO" },
    {
      title: "Senior Vice President - Reporting and Analytics",
      company: "Citi",
    },
    { title: "Head of Market Risk Analytics", company: "Mizuho" },
    {
      title: "Head of IT Transformation and AI Enablement",
      company: "Walgreens",
    },
  ],
  links: {
    panelle:
      "https://www.panelle.com/event-details/building-ai-ready-skills-women-leading-transformation",
  },
};

/* ============ Tiny Conduit “meter” (closed = 100%) ============ */
function ConduitMeter({ pct = 92, size = 120, label = "Seat Fill" }) {
  const gapDeg = Math.max(0, (100 - pct) * 3.6); // 0..360
  const cx = size / 2,
    cy = size / 2;
  const rOuter = size * 0.4;
  const rInner = rOuter * 0.55;

  const arc = (start, end) => {
    const a = (d) => ((d - 90) * Math.PI) / 180;
    const p = (r, d) => ({
      x: cx + r * Math.cos(a(d)),
      y: cy + r * Math.sin(a(d)),
    });
    const sweep = end - start;
    const large = sweep > 180 ? 1 : 0;
    const p1 = p(rOuter, start);
    const p2 = p(rOuter, end);
    const p3 = p(rInner, end);
    const p4 = p(rInner, start);
    return [
      `M ${p1.x} ${p1.y}`,
      `A ${rOuter} ${rOuter} 0 ${large} 1 ${p2.x} ${p2.y}`,
      `L ${p3.x} ${p3.y}`,
      `A ${rInner} ${rInner} 0 ${large} 0 ${p4.x} ${p4.y}`,
      "Z",
    ].join(" ");
  };

  // we “carve out” the gap, keep the rest filled
  const start = 30 + gapDeg; // rotate so gap sits ~2 o’clock
  const end = 30 + 360;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <rect x="0" y="0" width={size} height={size} fill="#fff" />
      <path
        d={arc(start, end)}
        fill={T.blue700}
        stroke="#fff"
        strokeWidth="2"
      />
      <text
        x={cx}
        y={cy - 4}
        fontFamily='"Source Sans Pro","Source Sans 3",system-ui,-apple-system,Segoe UI,Roboto,Arial'
        fontWeight="800"
        fontSize={22}
        textAnchor="middle"
        fill={T.blue975}
      >
        {pct}%
      </text>
      <text
        x={cx}
        y={cy + 18}
        fontFamily='"Source Sans Pro","Source Sans 3",system-ui,-apple-system,Segoe UI,Roboto,Arial'
        fontWeight="600"
        fontSize={12}
        textAnchor="middle"
        fill={T.muted}
      >
        {label}
      </text>
    </svg>
  );
}

/* ============ Dashboard ============ */
export default function RoundtableDashboard() {
  const totalRegs = EVENT.attendees.length;
  const cap = EVENT.cap;
  const confirmed = Math.min(totalRegs, cap);
  const waitlist = Math.max(0, totalRegs - cap);
  const seatFillPct = Math.round((confirmed / cap) * 100);

  const byCompany = useMemo(() => {
    const m = new Map();
    EVENT.attendees.forEach((a) =>
      m.set(a.company, (m.get(a.company) || 0) + 1)
    );
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, []);

  return (
    <section style={cardWrap()}>
      {/* Header */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={eyebrow()}>ABM Activity • Roundtable</div>
          <h2 style={{ margin: "4px 0 2px", color: T.blue975, fontSize: 22 }}>
            {" "}
            {EVENT.title}{" "}
          </h2>
          <div style={{ color: T.muted, fontSize: 13 }}>
            {EVENT.date} • {EVENT.track}
          </div>
          <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>
            {EVENT.partner}
          </div>
          <div
            style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}
          >
            {EVENT.topics.map((t) => (
              <span key={t} style={tag()}>
                {t}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <a
            href={EVENT.links.panelle}
            target="_blank"
            rel="noreferrer"
            style={btn("primary")}
          >
            Open event page
          </a>
        </div>
      </header>

      {/* KPI Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "160px 1fr 1fr 1fr",
          gap: 12,
          marginTop: 16,
        }}
      >
        <div style={kpiCard()}>
          <ConduitMeter pct={seatFillPct} label="Seat Fill" />
        </div>

        <div style={kpiCard()}>
          <div style={kpiLabel()}>Registrations</div>
          <div style={kpiValue()}>{totalRegs}</div>
          <div style={kpiHint()}>Across target accounts</div>
        </div>

        <div style={kpiCard()}>
          <div style={kpiLabel()}>Confirmed (cap {cap})</div>
          <div style={kpiValue()}>{confirmed}</div>
          <div style={kpiHint()}>Quality-first small group</div>
        </div>

        <div style={kpiCard()}>
          <div style={kpiLabel()}>Waitlist</div>
          <div style={kpiValue()}>{waitlist}</div>
          <div style={kpiHint()}>Follow-on session / nurture</div>
        </div>
      </div>

      {/* Speakers */}
      <div style={{ marginTop: 16, ...sectionCard() }}>
        <h3 style={secTitle()}>Speakers & Hosts</h3>
        <ul style={{ margin: 0, paddingLeft: 18, columns: 2 }}>
          {EVENT.speakers.map((s, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: T.ink }}>{s.name}</span>{" "}
              <span style={{ color: T.muted }}>
                — {s.role} ({s.kind})
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Attendee table + top companies */}
      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 12,
        }}
      >
        <div style={sectionCard()}>
          <h3 style={secTitle()}>Confirmed / Registrations</h3>
          <div
            style={{
              overflow: "auto",
              border: `1px solid ${T.line}`,
              borderRadius: 12,
            }}
          >
            <table style={tbl()}>
              <thead>
                <tr>
                  <th style={th()}>Job Title</th>
                  <th style={th()}>Company</th>
                </tr>
              </thead>
              <tbody>
                {EVENT.attendees.map((a, i) => (
                  <tr key={i}>
                    <td style={td()}>{a.title}</td>
                    <td style={td()}>{a.company}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>
            Note: We’ll cap live attendees for quality. Overflow will roll to a
            follow-up cohort.
          </div>
        </div>

        <div style={sectionCard()}>
          <h3 style={secTitle()}>Top Target Accounts (by count)</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {byCompany.slice(0, 8).map(([co, n]) => (
              <li key={co} style={{ marginBottom: 4 }}>
                <strong style={{ color: T.ink }}>{co}</strong>{" "}
                <span style={{ color: T.muted }}>— {n}</span>
              </li>
            ))}
          </ul>
          <div style={{ color: T.muted, fontSize: 12, marginTop: 8 }}>
            Use this to trigger AE/SDR pre-briefs and tailored outreach.
          </div>
        </div>
      </div>

      {/* Narrative */}
      <div style={{ marginTop: 16, ...sectionCard() }}>
        <h3 style={secTitle()}>Why this matters</h3>
        <p style={p()}>
          This pilot validates senior-level engagement on AI skills, with high
          interest across target accounts. Seat-fill is strong ({seatFillPct}%
          of cap) and the waitlist provides a follow-up path. We’ll brief
          AEs/SDRs on named attendees and run post-event follow-ups by account
          to progress opportunities and source new ones.
        </p>
      </div>
    </section>
  );
}

/* ============ tiny style helpers ============ */
function cardWrap() {
  return {
    background: T.card,
    border: `1px solid ${T.line}`,
    borderRadius: 16,
    padding: 16,
  };
}
function eyebrow() {
  return { color: T.muted, fontSize: 12, fontWeight: 600, letterSpacing: 0.2 };
}
function tag() {
  return {
    background: "#E9F2FF",
    color: "#2456A6",
    border: `1px solid ${T.line}`,
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 600,
  };
}
function btn(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${T.line}`,
    background: "#fff",
    color: T.ink,
    fontWeight: 700,
    textDecoration: "none",
    cursor: "pointer",
  };
  if (kind === "primary")
    return {
      ...base,
      background: T.blue700,
      color: "#fff",
      borderColor: T.blue700,
    };
  return base;
}
function kpiCard() {
  return {
    background: "#fff",
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    padding: 12,
    display: "grid",
    alignContent: "center",
    minHeight: 140,
  };
}
function kpiLabel() {
  return { color: T.muted, fontSize: 12, fontWeight: 600 };
}
function kpiValue() {
  return {
    color: T.blue975,
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.1,
    marginTop: 6,
  };
}
function kpiHint() {
  return { color: T.muted, fontSize: 12, marginTop: 2 };
}
function sectionCard() {
  return {
    background: "#fff",
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    padding: 12,
  };
}
function secTitle() {
  return { margin: "4px 0 10px", color: T.blue975, fontSize: 16 };
}
function p() {
  return { margin: 0, color: T.ink, lineHeight: 1.5 };
}
function tbl() {
  return {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily:
      '"Source Sans Pro","Source Sans 3",system-ui,-apple-system,Segoe UI,Roboto,Arial',
  };
}
function th() {
  return {
    textAlign: "left",
    background: "#F9FAFB",
    color: T.muted,
    fontSize: 12,
    fontWeight: 700,
    padding: "10px 12px",
    borderBottom: `1px solid ${T.line}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };
}
function td() {
  return {
    padding: "10px 12px",
    borderBottom: `1px solid ${T.line}`,
    color: T.ink,
    verticalAlign: "top",
  };
}
